import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/cms';
import Metadata from '../components/Metadata';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('om-os');
        setPageData(page);
      } catch (error) {
        console.error('Error loading about page:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--color-brand-cream]">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[--color-brand-green-dark]"></i>
          <p className="font-sans text-sm text-[--color-brand-muted] font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  const title = pageData?.meta_title || "Om os | BP haveservice";
  const description = pageData?.meta_description || "Lad os klare opgaven for dig Grundig kommunikation fra start...";

  const getOptimizedImageUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  return (
    <>
      <Metadata title={title} description={description} />

      {/* Header Section */}
      <section className="relative bg-[--color-brand-cream] overflow-hidden py-16 lg:py-24 px-6 flex items-center border-b border-[--color-brand-sand]">
        {/* Diagonal Sage Backdrop Split (Hero Left) */}
        <div 
          className="absolute inset-y-0 left-0 w-full lg:w-3/5 -skew-x-12 origin-top-left -translate-x-12 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, rgba(141, 164, 126, 0.35) 0%, rgba(76, 110, 59, 0.15) 100%)' }}
        ></div>

        {/* The "Peeking" Circle (Bottom-Left) */}
        <div 
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(45, 75, 28, 0.15)' }}
        ></div>

        {/* Organic Background Arch (Top Right) */}
        <div 
          className="absolute top-0 right-0 w-[30%] h-[50%] rounded-bl-[150px] z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(141, 164, 126, 0.12)' }}
        ></div>

        <div className="relative z-20 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Editorial Headings */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-2 font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              Hvem Er Vi
            </span>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-[--color-brand-charcoal] font-bold leading-tight">
              {pageData?.title || 'Om os'}
            </h1>
            <p className="font-sans text-base sm:text-lg text-[--color-brand-muted] max-w-xl leading-relaxed">
              {pageData?.subtitle || 'Lad os klare opgaven for dig'}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/kontakt"
                className="group inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-white text-[--color-brand-green-dark] border border-[--color-brand-green-dark]/20 hover:bg-white hover:text-[--color-brand-green-medium] hover:border-[--color-brand-green-medium] transition-all duration-300 text-sm font-semibold tracking-wide shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
              >
                <span>Kontakt os i dag</span>
                <i className="fa-solid fa-arrow-right ml-2.5 text-xs transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Right Column: Beautiful Leaf-shaped Image */}
          <div className="lg:col-span-5 relative flex flex-col items-center justify-center w-full max-w-md mx-auto overflow-visible">
            {/* Matrix Dot Pattern */}
            <div className="absolute -top-4 -right-4 grid grid-cols-6 gap-3 w-28 opacity-30 text-[--color-brand-green-dark] pointer-events-none z-0">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-current"></span>
              ))}
            </div>

            {/* Leaf Image Container */}
            <div className="relative w-full aspect-[4/3] sm:aspect-[1.2] rounded-tl-[100px] rounded-br-[100px] rounded-tr-3xl rounded-bl-3xl overflow-hidden shadow-xl border-4 border-white z-10 bg-[--color-brand-sage-pale]/20">
              <img
                src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-950758368.jpg", 800)}
                alt="Om os - BP Totalservice"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Decorative Leaf Element */}
            <div className="absolute -bottom-10 -left-10 w-24 h-24 pointer-events-none z-20 opacity-40">
              <img 
                src="https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/leaf-decor.png" 
                alt="Blad dekoration" 
                className="w-full h-full object-contain rotate-45"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left: Image Card */}
            <div className="lg:col-span-5 space-y-6">
              <div className="h-[30rem] lg:h-auto lg:aspect-3/4 rounded-3xl overflow-hidden shadow-md border border-[--color-brand-sand]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1223414721.jpg"
                  alt="BP Totalservice havearbejde"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-[--color-brand-cream] p-6 rounded-3xl border border-[--color-brand-sand] text-center">
                <p className="font-display text-2xl font-bold text-[--color-brand-green-dark]">30 44 16 11</p>
                <p className="font-sans text-xs uppercase tracking-wider text-[--color-brand-muted] font-bold mt-1">Ring for et uforpligtende tilbud</p>
              </div>
            </div>

            {/* Right: Rich Text Content */}
            <div className="lg:col-span-7">
              {pageData?.content ? (
                <div
                  className="prose-content"
                  dangerouslySetInnerHTML={{ __html: pageData.content }}
                />
              ) : (
                <div className="prose-content">
                  <h2>Lad os klare opgaven for dig</h2>
                  <h3>Grundig kommunikation fra start</h3>
                  <p>Vi har stor forståelse for, at vores kunders behov og ønsker kan variere, så vi tager altid en indledende snak med dig omkring netop dette. På den måde kan vi bedst muligt nå i mål med leveringen af en komplet og skræddersyet løsning. Vi spørger grundigt ind til dine forventninger til opgaven, så vi undgår misforståelse. Vi holder dig også gerne opdateret fra start til slut, hvis du ønsker det.</p>
                  <p>Det er vigtigt for os, at vores kunder føler sig trygge gennem hele forløbet.</p>
                  <h3>Høj kvalitet og god service</h3>
                  <p>Hos os kan du altid regne med god service og høj kvalitet. Vi går aldrig fra en opgave, før den 100% færdig, og før begge parter er tilfredse med resultatet. Vi overholder alle aftaler og deadlines, så hvis du er på udkig efter kvalitetsløsninger til tiden, så er det os, du skal kontakte.</p>
                  <p>Vores medarbejdere har den rette faglige viden og kompetencerne til at levere et veludført stykke arbejde. Vi møder dig altid med et smil, og vi besvarer med glæde ethvert spørgsmål, som du måtte have i løbet af projektet.</p>
                </div>
              )}

              <div className="mt-10 pt-8 border-t border-[--color-brand-sand] flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#BDC8B3] text-[--color-brand-green-dark] hover:bg-[--color-brand-green-dark] hover:text-white transition-colors text-sm font-semibold tracking-wide"
                >
                  Kontakt os i dag
                </Link>
                <Link
                  to="/ydelser"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-[--color-brand-sand] text-[--color-brand-charcoal] hover:bg-[--color-brand-cream] transition-colors text-sm font-semibold tracking-wide"
                >
                  Se vores ydelser
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
